import {
  normalizeCaptionPresentation,
  type CaptionAuthoredStyle,
} from "@desk/core";
import {
  computeFrameDrawGeometry,
  drawVideoWithGeometry,
  type FrameOpLike,
} from "./frame-geometry";
import { KINETIC_RAINBOW_STOPS } from "./creative-overlay-compose.js";

export interface ResultCardOptions {
  width: number;
  height: number;
  /** Optional video/poster frame; when absent, draws solid background only. */
  image?: CanvasImageSource | null;
  videoWidth?: number;
  videoHeight?: number;
  frame?: FrameOpLike | null;
  caption: string;
  /** Authored caption style for result-card typography (separate from WebGL). */
  captionStyle?: CaptionAuthoredStyle;
  captionScaleMilli?: number;
  captionAnchorXMilli?: number;
  captionAnchorYMilli?: number;
  /** e.g. "Reach 1840 · Cred -120" */
  scoreText: string;
  watermark: string;
  note?: string;
  /** When true, draws FICTIONAL RECONSTRUCTION on the card. */
  showReconstructionLabel?: boolean;
  /** Store cosmetic — default vs alternate portfolio chrome. */
  theme?: "default" | "portfolio-alt";
  /**
   * Optional callback invoked after the image (video frame / canvas) draw attempt.
   * Called with `true` if `drawImage` succeeded, `false` if it threw.
   * Callers that need to detect a silent draw failure (e.g. video CORS block,
   * decode race after the probe passed) should supply this and act accordingly.
   */
  reportImageDrawn?: (drawn: boolean) => void;
}

/** Stateful PNG result/still: frame treatment + caption + score + watermark. */
export function renderStatefulResultCard(options: ResultCardOptions): Promise<Blob> {
  const {
    width,
    height,
    image,
    videoWidth,
    videoHeight,
    frame,
    caption,
    captionStyle = "documentary",
    captionScaleMilli = 1000,
    captionAnchorXMilli = 500,
    captionAnchorYMilli = 820,
    scoreText,
    watermark,
    note,
    showReconstructionLabel = false,
    theme = "default",
    reportImageDrawn,
  } = options;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return Promise.reject(new Error("2d context unavailable"));
  }

  const alt = theme === "portfolio-alt";
  ctx.fillStyle = alt ? "#1a1520" : "#0d1a14";
  ctx.fillRect(0, 0, width, height);
  if (alt) {
    ctx.strokeStyle = "#d4a017";
    ctx.lineWidth = 4;
    ctx.strokeRect(6, 6, width - 12, height - 12);
  }

  if (image) {
    const vw =
      videoWidth ??
      ("videoWidth" in image && typeof (image as HTMLVideoElement).videoWidth === "number"
        ? (image as HTMLVideoElement).videoWidth || width
        : "naturalWidth" in image && typeof (image as HTMLImageElement).naturalWidth === "number"
          ? (image as HTMLImageElement).naturalWidth || width
          : width);
    const vh =
      videoHeight ??
      ("videoHeight" in image && typeof (image as HTMLVideoElement).videoHeight === "number"
        ? (image as HTMLVideoElement).videoHeight || height
        : "naturalHeight" in image && typeof (image as HTMLImageElement).naturalHeight === "number"
          ? (image as HTMLImageElement).naturalHeight || height
          : height);
    const geom = computeFrameDrawGeometry(frame, vw, vh, width, height);
    let imageDrawn = false;
    try {
      drawVideoWithGeometry(ctx, image, geom);
      imageDrawn = true;
    } catch {
      /* ignore decode races — caller can detect via reportImageDrawn */
    }
    reportImageDrawn?.(imageDrawn);
  }

  // Caption — drawn in 2D independently of the WebGL canvas. Per-style bold
  // treatment MIRRORS `.caption-overlay.style-*` in desk.css (and
  // `drawCaptionOverlay` in creative-overlay-compose.ts) exactly, so the
  // PUBLISHED still card matches the live editor overlay for the same
  // caption + style. Style is visual only — gameplay is untouched.
  if (caption) {
    const scale = captionScaleMilli / 1000;
    const cx = (captionAnchorXMilli / 1000) * width;
    const cy = (captionAnchorYMilli / 1000) * height;
    const boxW = Math.min(width - 24, width * 0.92);
    const boxH = Math.round(52 * scale);
    const fontSize = Math.round((captionStyle === "kinetic" ? 23 : 20) * scale);
    const lineHeight = Math.round((captionStyle === "kinetic" ? 25 : 22) * scale);
    const text = captionStyle === "urgent" ? caption.toUpperCase() : caption;

    // Background chrome per style — kinetic stays airy (no box), the others
    // get a bold band (documentary adds a thin "chyron" rule on the bottom
    // edge, urgent gets a red band).
    if (captionStyle === "urgent") {
      const band = ctx.createLinearGradient(cx, cy - boxH / 2, cx, cy + boxH / 2);
      band.addColorStop(0, "#e0201f");
      band.addColorStop(1, "#9c0d0d");
      ctx.fillStyle = band;
      ctx.fillRect(cx - boxW / 2, cy - boxH / 2, boxW, boxH);
    } else if (captionStyle === "documentary") {
      ctx.fillStyle = "rgba(17, 15, 11, 0.82)";
      ctx.fillRect(cx - boxW / 2, cy - boxH / 2, boxW, boxH);
      ctx.fillStyle = "rgba(244, 236, 216, 0.4)";
      ctx.fillRect(cx - boxW / 2, cy + boxH / 2 - 2, boxW, 2);
    } else if (captionStyle !== "kinetic") {
      // clean
      ctx.fillStyle = "rgba(9, 14, 24, 0.3)";
      ctx.fillRect(cx - boxW / 2, cy - boxH / 2, boxW, boxH);
    }

    if (captionStyle === "documentary") {
      ctx.font = `400 ${fontSize}px Newsreader, serif`;
    } else if (captionStyle === "kinetic") {
      ctx.font = `italic 800 ${fontSize}px Syne, sans-serif`;
    } else if (captionStyle === "urgent") {
      ctx.font = `800 ${fontSize}px Syne, sans-serif`;
    } else {
      ctx.font = `500 ${fontSize}px Space Grotesk, sans-serif`;
    }
    ctx.textAlign = "center";

    // Kinetic (tilt + rainbow gradient) and urgent (condensed scaleX) need a
    // local transform around the caption's center; the others draw straight.
    ctx.save();
    ctx.translate(cx, cy);
    if (captionStyle === "kinetic") ctx.rotate((-3 * Math.PI) / 180);
    if (captionStyle === "urgent") ctx.scale(0.94, 1);
    if (captionStyle === "kinetic") {
      const half = Math.max(boxW / 2 - 8, 10);
      const rainbow = ctx.createLinearGradient(-half, 0, half, 0);
      KINETIC_RAINBOW_STOPS.forEach((c, i) =>
        rainbow.addColorStop(i / (KINETIC_RAINBOW_STOPS.length - 1), c),
      );
      ctx.fillStyle = rainbow;
    } else if (captionStyle === "documentary") {
      ctx.fillStyle = "#f2ebdd";
    } else {
      ctx.fillStyle = "#ffffff";
    }
    wrapText(ctx, text, 0, -fontSize * 0.2, boxW - 16, lineHeight);
    ctx.restore();
  }

  // Score
  ctx.textAlign = "left";
  ctx.fillStyle = alt ? "rgba(212,160,23,0.92)" : "rgba(11,18,32,0.7)";
  ctx.fillRect(12, 12, Math.min(width - 24, 220), 36);
  ctx.fillStyle = alt ? "#1a1520" : "#f4faf3";
  ctx.font = "600 14px IBM Plex Mono, monospace";
  ctx.fillText(scoreText || "Reach –", 20, 35);

  if (showReconstructionLabel) {
    const label = "FICTIONAL RECONSTRUCTION";
    ctx.font = "700 11px Syne, sans-serif";
    const tw = ctx.measureText(label).width + 16;
    ctx.fillStyle = "rgba(196, 92, 38, 0.92)";
    ctx.fillRect(width - tw - 12, 12, tw, 28);
    ctx.fillStyle = "#fff8f2";
    ctx.textAlign = "right";
    ctx.fillText(label, width - 20, 31);
    ctx.textAlign = "left";
  }

  // Watermark
  ctx.fillStyle = "rgba(11,18,32,0.65)";
  ctx.fillRect(12, height - 36, 88, 22);
  ctx.fillStyle = "#ffffff";
  ctx.font = "12px Syne, sans-serif";
  ctx.fillText(watermark, 20, height - 20);

  if (note) {
    ctx.fillStyle = "rgba(244,250,243,0.85)";
    ctx.font = "10px IBM Plex Mono, monospace";
    ctx.textAlign = "right";
    ctx.fillText(note, width - 12, height - 14);
  }

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
          return;
        }
        // Headless / limited canvas: fall back to toDataURL so we still emit
        // a watermarked PNG rather than an unwatermarked raw frame.
        try {
          const dataUrl = canvas.toDataURL("image/png");
          const b64 = dataUrl.split(",")[1];
          if (!b64) {
            reject(new Error("toBlob failed"));
            return;
          }
          const bin = atob(b64);
          const bytes = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
          resolve(new Blob([bytes], { type: "image/png" }));
        } catch (err) {
          reject(err instanceof Error ? err : new Error("toBlob failed"));
        }
      },
      "image/png",
    );
  });
}

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
): void {
  const words = text.split(/\s+/);
  let line = "";
  let row = 0;
  for (const word of words) {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && line) {
      ctx.fillText(line, x, y + row * lineHeight);
      line = word;
      row += 1;
      if (row >= 2) break;
    } else {
      line = test;
    }
  }
  if (line && row < 2) ctx.fillText(line, x, y + row * lineHeight);
}

/** Resolve caption presentation for result-card from the last active text op. */
export function resultCardCaptionPresentation(
  captions: Array<{
    style?: CaptionAuthoredStyle;
    scaleMilli?: number;
    anchorXMilli?: number;
    anchorYMilli?: number;
    startTick: number;
    endTick: number;
    tokenIds: string[];
  }>,
  sourceTick: number,
) {
  const active = captions.filter(
    (c) => sourceTick >= c.startTick && sourceTick < c.endTick,
  );
  const last = active[active.length - 1];
  if (!last) {
    return normalizeCaptionPresentation({
      kind: "text",
      opId: "none",
      captionId: "none",
      tokenIds: [],
      startTick: 0,
      endTick: 1,
    });
  }
  return normalizeCaptionPresentation({
    kind: "text",
    opId: "card",
    captionId: "card",
    tokenIds: last.tokenIds,
    startTick: last.startTick,
    endTick: last.endTick,
    style: last.style,
    scaleMilli: last.scaleMilli,
    anchorXMilli: last.anchorXMilli,
    anchorYMilli: last.anchorYMilli,
  });
}
